import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { PageHeader } from '../../../shared/components/PageHeader';
import { Spinner } from '../../../shared/components/Spinner';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchCompanyOptions, type CompanyOption } from '../../../services/companies/companyODataApi';
import { inviteMerchant } from '../../../services/merchants/adminMerchantApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function MerchantInvitePage(): React.ReactElement {
  const { acquireAccessToken } = useAuth();
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [suggestedMerchantName, setSuggestedMerchantName] = useState('');
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useDocumentTitle('Invite merchant · Triplicity Backoffice');

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    setCompaniesError(null);
    try {
      const list = await fetchCompanyOptions(acquireAccessToken);
      setCompanies(list);
    } catch (e) {
      setCompanies([]);
      setCompaniesError(e instanceof Error ? e.message : 'Failed to load companies.');
    } finally {
      setCompaniesLoading(false);
    }
  }, [acquireAccessToken]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  const validate = (): boolean => {
    const next: Partial<Record<string, string>> = {};
    const em = email.trim();
    if (!em) {
      next.email = 'Email is required.';
    } else if (!EMAIL_RE.test(em)) {
      next.email = 'Enter a valid email address.';
    }
    const name = suggestedMerchantName.trim();
    if (!name) {
      next.suggestedMerchantName = 'Suggested merchant name is required.';
    }
    if (companyId === undefined || companyId === '') {
      next.companyId = 'Select a company.';
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);
    setSuccess(null);
    if (!validate()) {
      return;
    }
    if (companyId === undefined || companyId === '') {
      return;
    }
    setSubmitting(true);
    try {
      await inviteMerchant(
        {
          email: email.trim(),
          suggestedMerchantName: suggestedMerchantName.trim(),
          companyId,
        },
        acquireAccessToken
      );
      setSuccess('Invitation sent successfully.');
      setEmail('');
      setSuggestedMerchantName('');
      setCompanyId(undefined);
      setFieldErrors({});
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Invitation failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bo-detail-page">
      <PageHeader
        title="Invite merchant"
        subtitle="Send an invitation to join as a merchant for a company."
      />

      <Link className="bo-detail-back-link" to="/merchants">
        ← Back to merchants
      </Link>

      {companiesLoading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading companies…" />
        </div>
      ) : null}

      {!companiesLoading && companiesError ? <ErrorAlert message={companiesError} /> : null}

      {!companiesLoading && !companiesError ? (
        <form className="bo-card bo-form-card" onSubmit={(e) => void handleSubmit(e)}>
          <h2 className="bo-form-section-title">Invitation</h2>
          <p className="bo-form-hint">
            Choose the company the merchant will belong to, then enter their email and a suggested
            business name.
          </p>
          <div className="bo-form-fields">
            <div className="bo-field">
              <label className="bo-field-label" htmlFor="invite-email">
                Email
              </label>
              <input
                id="invite-email"
                className="bo-input"
                type="email"
                value={email}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                  setFieldErrors((p) => {
                    const { email: _, ...rest } = p;
                    return rest;
                  });
                }}
                autoComplete="email"
              />
              {fieldErrors.email ? (
                <span className="bo-field-error" role="alert">
                  {fieldErrors.email}
                </span>
              ) : null}
            </div>
            <div className="bo-field">
              <label className="bo-field-label" htmlFor="invite-name">
                Suggested merchant name
              </label>
              <input
                id="invite-name"
                className="bo-input"
                type="text"
                value={suggestedMerchantName}
                onChange={(ev) => {
                  setSuggestedMerchantName(ev.target.value);
                  setFieldErrors((p) => {
                    const { suggestedMerchantName: _, ...rest } = p;
                    return rest;
                  });
                }}
                autoComplete="organization"
              />
              {fieldErrors.suggestedMerchantName ? (
                <span className="bo-field-error" role="alert">
                  {fieldErrors.suggestedMerchantName}
                </span>
              ) : null}
            </div>
            <div className="bo-field">
              <span className="bo-field-label" id="invite-company-label">
                Company
              </span>
              <div className="bo-invite-ddl-wrap">
                <DropDownListComponent
                  id="invite-company"
                  dataSource={companies as unknown as { [key: string]: Object }[]}
                  fields={{ text: 'text', value: 'value' }}
                  placeholder="Select company"
                  allowFiltering
                  filterBarPlaceholder="Search companies"
                  popupHeight="240px"
                  value={companyId}
                  change={(args) => {
                    setCompanyId(
                      args.value != null && args.value !== '' ? String(args.value) : undefined
                    );
                    setFieldErrors((p) => {
                      const { companyId: _, ...rest } = p;
                      return rest;
                    });
                  }}
                  cssClass="bo-invite-company-ddl"
                />
              </div>
              {fieldErrors.companyId ? (
                <span className="bo-field-error" role="alert">
                  {fieldErrors.companyId}
                </span>
              ) : null}
            </div>
          </div>
          {success ? (
            <p className="bo-form-success" role="status">
              {success}
            </p>
          ) : null}
          {submitError ? <ErrorAlert message={submitError} /> : null}
          <div className="bo-actions" style={{ marginTop: '1.25rem' }}>
            <button type="submit" className="bo-btn bo-btn-primary" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
